import portfolio from "../../content/portfolio.json";
import { classifyHorizontalSwipe } from "../core/gestures.js";

const DIALOG_TRANSITION_DURATION = 360;
const INTERACTIVE_TARGET_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "[contenteditable]",
  "[role='button']",
  "[data-no-swipe]"
].join(",");

function createList(items, { className = "", dataAttribute = "" } = {}) {
  const list = document.createElement("ul");
  if (className) {
    list.className = className;
  }
  if (dataAttribute) {
    list.setAttribute(dataAttribute, "");
  }
  items.forEach((item) => {
    const entry = document.createElement("li");
    entry.textContent = item;
    list.append(entry);
  });
  return list;
}

function createMedia(project) {
  const picture = document.createElement("picture");
  picture.className = "project-detail__media";
  const image = document.createElement("img");
  image.src = project.media.src;
  image.srcset = project.media.srcSet;
  image.sizes = "(max-width: 48rem) 92vw, 900px";
  image.width = project.media.width;
  image.height = project.media.height;
  image.alt = project.media.alt;
  image.decoding = "async";
  image.style.objectPosition = project.media.objectPosition;
  picture.append(image);
  return picture;
}

function createDetailColumn(titleText, items, dataAttribute) {
  const section = document.createElement("section");
  const title = document.createElement("h3");
  title.textContent = titleText;
  section.append(title, createList(items, { dataAttribute }));
  return section;
}

export class ProjectDialogController {
  #dialog;
  #content;
  #store;
  #scroll;
  #cleanup = [];
  #lastTrigger = null;
  #activeIndex = -1;
  #touchStart = null;
  #transitionTimer = null;

  constructor({ dialog, store, scroll }) {
    this.#dialog = dialog;
    this.#content = dialog.querySelector("[data-dialog-content]");
    this.#store = store;
    this.#scroll = scroll;
  }

  init() {
    document.querySelectorAll("[data-open-project]").forEach((button) => {
      const handler = () => this.open(button.dataset.openProject, button);
      button.addEventListener("click", handler);
      this.#cleanup.push(() =>
        button.removeEventListener("click", handler)
      );
    });

    const closeButton = this.#dialog.querySelector("[data-close-dialog]");
    const closeHandler = () => this.close();
    closeButton.addEventListener("click", closeHandler);
    this.#cleanup.push(() =>
      closeButton.removeEventListener("click", closeHandler)
    );

    const dialogClickHandler = (event) => {
      const rect = this.#dialog.getBoundingClientRect();
      const outside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (outside) {
        this.close();
      }
    };
    this.#dialog.addEventListener("click", dialogClickHandler);
    this.#cleanup.push(() =>
      this.#dialog.removeEventListener("click", dialogClickHandler)
    );

    const cancelHandler = (event) => {
      event.preventDefault();
      this.close();
    };
    this.#dialog.addEventListener("cancel", cancelHandler);
    this.#cleanup.push(() =>
      this.#dialog.removeEventListener("cancel", cancelHandler)
    );

    const keyHandler = (event) => {
      if (!this.#dialog.open) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.#navigate(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.#navigate(1);
      }
    };
    window.addEventListener("keydown", keyHandler);
    this.#cleanup.push(() =>
      window.removeEventListener("keydown", keyHandler)
    );

    const touchStartHandler = (event) => {
      if (
        !this.#dialog.open ||
        event.touches.length !== 1 ||
        event.target.closest?.(INTERACTIVE_TARGET_SELECTOR)
      ) {
        this.#touchStart = null;
        return;
      }
      const touch = event.touches[0];
      this.#touchStart = {
        startX: touch.clientX,
        startY: touch.clientY
      };
    };
    const touchEndHandler = (event) => {
      const start = this.#touchStart;
      this.#touchStart = null;
      if (!start || event.changedTouches.length !== 1) {
        return;
      }
      const touch = event.changedTouches[0];
      const direction = classifyHorizontalSwipe(
        {
          ...start,
          endX: touch.clientX,
          endY: touch.clientY
        },
        {
          threshold: 60,
          dominance: 1.25
        }
      );
      if (direction !== 0) {
        this.#navigate(-direction);
      }
    };
    const touchCancelHandler = () => {
      this.#touchStart = null;
    };
    this.#content.addEventListener("touchstart", touchStartHandler, {
      passive: true
    });
    this.#content.addEventListener("touchend", touchEndHandler, {
      passive: true
    });
    this.#content.addEventListener("touchcancel", touchCancelHandler, {
      passive: true
    });
    this.#cleanup.push(() => {
      this.#content.removeEventListener("touchstart", touchStartHandler);
      this.#content.removeEventListener("touchend", touchEndHandler);
      this.#content.removeEventListener("touchcancel", touchCancelHandler);
    });

    const hashHandler = () => {
      const match = window.location.hash.match(/^#project\/(.+)$/);
      if (match) {
        this.open(decodeURIComponent(match[1]), null, {
          updateHash: false
        });
      } else if (this.#dialog.open) {
        this.close({ updateHash: false });
      }
    };
    window.addEventListener("hashchange", hashHandler);
    this.#cleanup.push(() =>
      window.removeEventListener("hashchange", hashHandler)
    );
    hashHandler();
  }

  #render(project) {
    this.#content.replaceChildren();
    const article = document.createElement("article");
    article.className = "project-detail";
    article.style.setProperty("--project-accent", project.media.accent);

    const meta = document.createElement("div");
    meta.className = "project-detail__meta";
    const english = document.createElement("span");
    english.textContent = project.englishTitle;
    const period = document.createElement("span");
    period.textContent = project.period;
    const progress = document.createElement("span");
    progress.className = "project-detail__progress";
    progress.setAttribute("data-dialog-project-progress", "");
    progress.setAttribute(
      "aria-label",
      `第 ${this.#activeIndex + 1} 个项目，共 ${portfolio.projects.length} 个`
    );
    progress.textContent = `${String(this.#activeIndex + 1).padStart(
      2,
      "0"
    )} / ${String(portfolio.projects.length).padStart(2, "0")}`;
    meta.append(english, period, progress);

    const title = document.createElement("h2");
    title.id = "dialog-title";
    title.textContent = project.title;

    const role = document.createElement("p");
    role.className = "project-detail__role";
    role.setAttribute("data-project-role", "");
    role.textContent = project.role;

    const summary = document.createElement("p");
    summary.className = "project-detail__summary";
    summary.textContent = project.summary;

    const description = document.createElement("p");
    description.className = "project-detail__description";
    description.textContent = project.description;

    const columns = document.createElement("div");
    columns.className = "project-detail__columns";
    columns.append(
      createDetailColumn(
        "RESPONSIBILITIES",
        project.responsibilities,
        "data-project-responsibilities"
      ),
      createDetailColumn(
        "OUTCOMES",
        project.outcomes,
        "data-project-outcomes"
      )
    );

    const stack = createList(project.stack, {
      className: "project-detail__stack",
      dataAttribute: "data-project-stack"
    });

    const navigation = document.createElement("nav");
    navigation.className = "project-detail__navigation";
    navigation.setAttribute("aria-label", "项目详情导航");
    const projectCount = portfolio.projects.length;
    const previousProject =
      portfolio.projects[
        (this.#activeIndex - 1 + projectCount) % projectCount
      ];
    const nextProject =
      portfolio.projects[(this.#activeIndex + 1) % projectCount];
    const previous = document.createElement("button");
    previous.type = "button";
    previous.setAttribute("aria-label", "上一个项目");
    previous.dataset.projectTarget = previousProject.id;
    previous.textContent = `上一个项目 · ${previousProject.title}`;
    previous.addEventListener("click", () => this.#navigate(-1));
    const next = document.createElement("button");
    next.type = "button";
    next.setAttribute("aria-label", "下一个项目");
    next.dataset.projectTarget = nextProject.id;
    next.textContent = `下一个项目 · ${nextProject.title}`;
    next.addEventListener("click", () => this.#navigate(1));
    navigation.append(previous, next);

    article.append(
      createMedia(project),
      meta,
      title,
      role,
      summary,
      description,
      columns,
      stack,
      navigation
    );
    this.#content.append(article);
  }

  #navigate(offset) {
    if (this.#activeIndex < 0) {
      return;
    }
    const count = portfolio.projects.length;
    const nextIndex = (this.#activeIndex + offset + count) % count;
    this.open(portfolio.projects[nextIndex].id, null, {
      replaceHash: true,
      transitionDirection: offset > 0 ? "next" : "previous"
    });
  }

  #setTransitionDirection(direction) {
    window.clearTimeout(this.#transitionTimer);
    if (!direction) {
      this.#content.classList.remove("is-transitioning");
      delete this.#content.dataset.transitionDirection;
      return;
    }
    this.#content.classList.add("is-transitioning");
    this.#content.dataset.transitionDirection = direction;
    this.#transitionTimer = window.setTimeout(() => {
      this.#content.classList.remove("is-transitioning");
      delete this.#content.dataset.transitionDirection;
      this.#transitionTimer = null;
    }, DIALOG_TRANSITION_DURATION);
  }

  open(
    id,
    trigger = null,
    {
      updateHash = true,
      replaceHash = false,
      transitionDirection = null
    } = {}
  ) {
    const projectIndex = portfolio.projects.findIndex((item) => item.id === id);
    if (projectIndex < 0) {
      return false;
    }
    const project = portfolio.projects[projectIndex];
    const wasOpen = this.#dialog.open;
    if (trigger) {
      this.#lastTrigger = trigger;
    }
    this.#activeIndex = projectIndex;
    this.#setTransitionDirection(wasOpen ? transitionDirection : null);
    this.#render(project);
    this.#scroll.pause(true);
    document.body.classList.add("dialog-open");
    this.#store.patch(
      { dialog: { projectId: project.id } },
      "project:open"
    );
    if (!wasOpen) {
      this.#dialog.showModal();
    }
    if (updateHash) {
      const method = replaceHash || wasOpen ? "replaceState" : "pushState";
      window.history[method](
        { projectId: project.id },
        "",
        `#project/${encodeURIComponent(project.id)}`
      );
    }
    return true;
  }

  close({ updateHash = true } = {}) {
    if (!this.#dialog.open) {
      return;
    }
    this.#dialog.close();
    this.#scroll.pause(false);
    document.body.classList.remove("dialog-open");
    this.#touchStart = null;
    this.#setTransitionDirection(null);
    this.#activeIndex = -1;
    this.#store.patch(
      { dialog: { projectId: null } },
      "project:close"
    );
    if (updateHash && window.location.hash.startsWith("#project/")) {
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${window.location.search}`
      );
    }
    this.#lastTrigger?.focus();
  }

  getActiveProject() {
    return this.#activeIndex < 0
      ? null
      : portfolio.projects[this.#activeIndex].id;
  }

  destroy() {
    window.clearTimeout(this.#transitionTimer);
    this.#cleanup.forEach((cleanup) => cleanup());
  }
}
